import axios, {AxiosError, AxiosRequestConfig} from "axios";
import {Reducer} from 'redux';
import {Dispatch} from "react";

// @ts-ignore
const publicHost = window.state?.publicURL

const initialState: AppState = {
  application: null,
  data: null,
  session: null,
}

interface Action {
  type: ActionTypes.UPSERT_STATE
  state?: AppState | null
}

enum ActionTypes {
  UPSERT_STATE = 'UPSERT_STATE',
}

export const reducer: Reducer<AppState, Action> = (state = initialState, action) => {
  switch (action.type) {
    case ActionTypes.UPSERT_STATE:
      return {
        ...state,
        ...action.state,
        errors: action.state?.errors,
      }
    default:
      return state
  }
}

export interface Address {
  street1?: string
  street2?: string
  city?: string
  county?: string
  state?: string
  postalCode?: string
}


export interface Application {
  id?: string
  address?: Address
  bankAccount?: BankAccount
  businessLegalName?: string
  businessNickname?: string
  businessPhoneNumber?: string
  businessType?: string
  counties?: [string]
  email?: string
  federalClassification?: FederalClassification
  firstName?: string
  hasBankInfo?: boolean
  hasTIN?: boolean
  hasAcceptedContract?: boolean
  hasVerifiedPhoneNumber?: boolean
  homeState?: string
  lastName?: string
  legalName?: string
  phoneNumber?: string
  trades?: [string] | null
  website?: string
  status?: string
  createdAt?: DateTime
  updatedAt?: DateTime
}

export interface ApplicationContract {
  contractText?: string
  contractType?: string
  contractVersion?: string
}

export interface AppState {
  addressAutocomplete?: [{address: Address}] | null
  application?: Application | null
  applicationContracts?: [ApplicationContract] | null
  createLinkToken?: CreateLinkToken | null
  errors?: [Error] | null
  federalClassifications?: [FederalClassifications] | null
  data?: AppState | null
  regions?: [Region] | null
  session?: Session | null
  trades?: [County] | null
}

export interface BankAccount {
  routingNumber?: string
}

export interface County {
  id: string
  name: string
}

export interface CreateLinkToken {
  token: string
}

export interface DateTime {
  custom: string
  date: string
  time: string
}

export interface Error {
  message: string
  path: [string]
  extensions?: ErrorExtensions
}

export interface ErrorExtensions {
  code: string
  message: string
}

export interface FederalClassifications {
  label: string
  value: string
  subtypes?: [Option]
}

export interface FederalClassification {
  type: Option
  subtype?: Option
}

export interface Option {
  label: string
  value: string
}

export interface Region {
  id: string
  name: string
  counties: [County]
}

export interface Session {
  user?: User;
}

export interface Trade {
  id: string
  name: string
}

export interface User {
  picture?: string;
  primaryEmail?: string;
}

export interface Params {
  variables?: { [key: string]: any };
  query: string;
  onSuccess?: (content: AppState) => void;
  onFailure?: (error: any) => void;
  // when set indicates that errors should redirect user to signup page rather than login page
  useSignupURL?: boolean;
  usePublicHost?: boolean;
}

export const gql = ({variables, query, onSuccess, onFailure, useSignupURL, usePublicHost}: Params) => {
  return async (dispatch: Dispatch<Action>) => {
    let url = "/graphql"
    if (usePublicHost) {
      url = `${publicHost}/pub${url}`
    }

    const axiosConfig: AxiosRequestConfig = {
      url: url,
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      data: {query, variables},
    };

    try {
      const response = await axios.request<AppState>(axiosConfig);

      // errors have priority over data
      //
      if (response.data?.errors) {
        dispatch({type: ActionTypes.UPSERT_STATE, state: response.data})
        if (onFailure != null) {
          onFailure(response.data?.errors)
        }
        return
      }

      // handle data
      //
      let data = response.data;
      while (data && data.data != null) {
        data = data.data
      }
      dispatch({type: ActionTypes.UPSERT_STATE, state: data})

      if (onSuccess != null) {
        onSuccess(data)
      }

    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 403) {
          if (useSignupURL) {
            document.location = '/signup'

          } else {
            document.location = '/login'
          }
        }
      }

      if (onFailure != null) {
        onFailure(error)
      }
      throw error
    }
  }
}

const applicationFragment = `
fragment ApplicationParts on Application {
  id
  address {
    street1
    street2
    city
    state
    postalCode
  }
  bankAccount {
    routingNumber
  }
  businessLegalName
  businessNickname
  businessPhoneNumber
  businessType
  counties
  email
  federalClassification {
    type {
      label
      value
    }
    subtype {
      label
      value
    }
  }
  firstName
  hasBankInfo
  hasTIN
  hasAcceptedContract
  hasVerifiedPhoneNumber
  homeState
  lastName
  legalName
  phoneNumber
  status
  trades
  website
  createdAt {
    date
    time
  }
  updatedAt {
    date
    time
  }
}

`
const sessionFragment = `
fragment SessionParts on Session {
  user {
    firstName
    lastName
    fullName
    picture
    primaryEmail
  }
}
`


export const emailApplicationContract = `
mutation op($id: String!) {
  applicationContractEmail(id: $id){
    ok
  }
}
`

export const contractSign = `
mutation contractSign($applicationID: String!, $signatureName: String!) {
  contractSign(form: {applicationID: $applicationID, signatureName: $signatureName}) {
    applicationID
  }
}
`

export const queryAddressAutoComplete = `
query op($query: String) {
  addressAutocomplete(query: $query) {
    address {
      street1
      street2
      city
      county
      state
      postalCode
    }
  }
}
`

export const queryApplication = `
query op($id: String!) {
  application(id: $id) {
    ...ApplicationParts
  }
  session {
    ...SessionParts
  }
}

${applicationFragment}
${sessionFragment}
`

export const queryApplicationWithContracts = `
query op($id: String!) {
  application(id: $id) {
    ...ApplicationParts
  }
  applicationContracts(id: $id) {
    contractText
    contractType
    contractVersion
  }
  session {
    ...SessionParts
  }
}

${applicationFragment}
${sessionFragment}
`;

export const queryRegions = `
query op($states: [String!], $activeOnly: Boolean = false){
  regions(states: $states, activeOnly: $activeOnly) {
    id
    name
    counties {
      id
      name
    }
  }
}
`

export const queryApplicationWithFederalClassifications = `
query op($id: String!, $federalClassificationGroupType: FederalClassificationGroupType!) {
  application(id: $id) {
    ...ApplicationParts
  }
  session {
    ...SessionParts
  }

  federalClassifications(federalClassificationGroupType: $federalClassificationGroupType) {
    label
    value
    subtypes {
      label
      value
    }
  }
}

${applicationFragment}
${sessionFragment}
`

export const queryTrades = `
query op {
  trades {
    id
    name
  }
}
`


export const updateApplication = `
mutation op($id: String!, $form: ApplicationInput!) {
  application: applicationUpdate(id: $id, form: $form) {
    ...ApplicationParts
  }
}

${applicationFragment}
`

export const updateApplicationUpdateContactInfo = `
mutation op($id: String!, $form: ApplicationContactInfoInput!) {
  application: applicationUpdateContactInfo(id: $id, form: $form) {
    ...ApplicationParts
  }
}

${applicationFragment}
`

export const updateApplicationUpdateTaxInfo = `
mutation op($id: String!, $tin: String!, $tinType: TINType!) {
  application: applicationUpdateUpdateTaxInfo(id: $id, tin: $tin, tinType: $tinType) {
    ...ApplicationParts
  }
}

${applicationFragment}
`

export const plaidLinkTokenCreate = `
mutation op($applicationID: String!) {
  createLinkToken(applicationID: $applicationID) {
    token
  }
}
`

export const verificationCodeConfirm = `
mutation op($id: String!, $code: String!) {
  confirmVerificationCode(applicationID: $id, verificationCode: $code) {
    ok
  }
}
`

export const verificationCodeResend = `
mutation op($id: String!) {
  resendVerificationCode(applicationID: $id) {
    ok
  }
}
`
